import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle2, 
  Calendar, 
  Clock, 
  User, 
  Briefcase, 
  Building2, 
  ArrowRight, 
  ArrowLeft, 
  FileText, 
  AlertCircle,
  Copy,
  Check
} from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../common/Toast';
import { formatUzPhone, isCompleteUzPhone } from '../../utils/helpers';
import TimeSlotPicker from './TimeSlotPicker';

export default function BookingWizard() {
  const { t, localized, currentLang } = useLanguage();
  const [searchParams] = useSearchParams();

  // Wizard step state (1 to 7)
  const [step, setStep] = useState(1);

  // Entities state
  const [services, setServices] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);

  // Booking form state
  const [formData, setFormData] = useState({
    serviceId: '',
    lawyerId: '',
    officeId: '',
    appointmentDate: '',
    startTime: '',
    clientName: '',
    clientPhone: '+998 ()',
    clientEmail: '',
    clientReason: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [copiedRef, setCopiedRef] = useState(false);

  // Load available services, lawyers, offices
  useEffect(() => {
    Promise.all([
      api.get('/services'),
      api.get('/lawyers'),
      api.get('/offices'),
    ])
      .then(([sRes, lRes, oRes]) => {
        if (sRes?.success) setServices(sRes.services || []);
        if (lRes?.success) setLawyers(lRes.lawyers || []);
        if (oRes?.success) setOffices(oRes.offices || []);

        // Pre-select if URL params passed
        const paramLawyerId = searchParams.get('lawyer');
        const paramServiceId = searchParams.get('service');
        const paramOfficeId = searchParams.get('office');

        setFormData((prev) => ({
          ...prev,
          lawyerId: paramLawyerId || (lRes?.lawyers?.[0]?.id ? String(lRes.lawyers[0].id) : ''),
          serviceId: paramServiceId || (sRes?.services?.[0]?.id ? String(sRes.services[0].id) : ''),
          officeId: paramOfficeId || (oRes?.offices?.[0]?.id ? String(oRes.offices[0].id) : ''),
        }));

        if (paramServiceId && paramLawyerId) {
          setStep(3); // jump to office
        } else if (paramServiceId) {
          setStep(2);
        }
      })
      .catch((err) => {
        showToast(err.message, 'error');
      })
      .finally(() => {
        setLoadingInitial(false);
      });
  }, [searchParams]);

  // Selected object getters
  const selectedService = services.find((s) => String(s.id) === String(formData.serviceId));
  const selectedLawyer = lawyers.find((l) => String(l.id) === String(formData.lawyerId));
  const selectedOffice = offices.find((o) => String(o.id) === String(formData.officeId));

  // Minimum date = tomorrow (or today if urgent)
  const todayStr = new Date().toISOString().split('T')[0];

  // Validation per step
  const canProceed = () => {
    switch (step) {
      case 1:
        return Boolean(formData.serviceId);
      case 2:
        return Boolean(formData.lawyerId);
      case 3:
        return Boolean(formData.officeId);
      case 4:
        return Boolean(formData.appointmentDate && formData.appointmentDate >= todayStr && formData.startTime);
      case 5:
        return (
          formData.clientName.trim().length >= 2 &&
          isCompleteUzPhone(formData.clientPhone)
        );
      case 6:
        return formData.clientReason.trim().length >= 10;
      case 7:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canProceed()) return;

    setSubmitting(true);
    try {
      const payload = {
        serviceId: parseInt(formData.serviceId, 10),
        lawyerId: parseInt(formData.lawyerId, 10),
        officeId: parseInt(formData.officeId, 10),
        appointmentDate: formData.appointmentDate,
        startTime: formData.startTime,
        clientName: formData.clientName.trim(),
        clientPhone: formData.clientPhone.trim(),
        clientEmail: formData.clientEmail?.trim() || `${formData.clientPhone.replace(/\D/g, '') || 'mijoz'}@advokat.uz`,
        clientReason: formData.clientReason.trim(),
      };

      const res = await api.post('/public/appointments', payload);
      if (res?.success) {
        setConfirmedBooking(res.appointment || res);
        showToast('Arizangiz muvaffaqiyatli qabul qilindi!', 'success');
      } else {
        showToast(res?.message || 'Xatolik yuz berdi', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const copyReference = () => {
    if (confirmedBooking?.bookingReference) {
      navigator.clipboard.writeText(confirmedBooking.bookingReference);
      setCopiedRef(true);
      setTimeout(() => setCopiedRef(false), 2000);
      showToast('Bron raqami nusxalandi', 'info');
    }
  };

  // SUCCESS CONFIRMATION VIEW
  if (confirmedBooking) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden p-6 sm:p-10 max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-200">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
        </div>

        <h2 className="font-serif text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
          {t('booking_success_title', 'Arizangiz muvaffaqiyatli qabul qilindi!')}
        </h2>
        <p className="text-slate-600 text-sm max-w-md mx-auto mb-8">
          {t('booking_next_step', 'Advokatlar ma’muriyati arizangizni ko‘rib chiqib, tasdiqlash xabarnomasini yuboradi.')}
        </p>

        {/* Reference Box */}
        <div className="bg-slate-50 border-2 border-dashed border-[#c5a880] p-6 rounded-lg mb-8 inline-block w-full max-w-md">
          <span className="text-xs uppercase tracking-wider text-slate-500 block mb-1 font-semibold">
            {t('booking_ref_label', 'Sizning bron raqamingiz:')}
          </span>
          <div className="flex items-center justify-center space-x-2">
            <span className="font-mono text-2xl sm:text-3xl font-bold text-[#091322]">
              {confirmedBooking.bookingReference}
            </span>
            <button
              onClick={copyReference}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-600 transition-colors"
              title="Nusxalash"
            >
              {copiedRef ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <div className="mt-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
            <span className="w-2 h-2 rounded-full bg-amber-500 mr-1.5 animate-pulse"></span>
            Holat: Kutilmoqda (Pending confirmation)
          </div>
        </div>

        {/* Summary details */}
        <div className="text-left bg-slate-50 border border-slate-200 rounded-lg p-5 mb-8 text-sm space-y-2">
          <div className="flex justify-between py-1 border-b border-slate-200">
            <span className="text-slate-500">Mijoz:</span>
            <span className="font-medium text-slate-800">{confirmedBooking.clientName}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-slate-200">
            <span className="text-slate-500">Advokat:</span>
            <span className="font-medium text-slate-800">{selectedLawyer?.fullName}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-slate-200">
            <span className="text-slate-500">Xizmat:</span>
            <span className="font-medium text-slate-800">{localized(selectedService, 'title')}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-slate-200">
            <span className="text-slate-500">Sana va vaqt:</span>
            <span className="font-medium text-slate-800">
              {confirmedBooking.appointmentDate} soat {confirmedBooking.startTime}
            </span>
          </div>
          <div className="flex justify-between py-1">
            <span className="text-slate-500">Ofis:</span>
            <span className="font-medium text-slate-800">{selectedOffice?.name}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={`/check-status?ref=${confirmedBooking.bookingReference}&contact=${encodeURIComponent(
              confirmedBooking.clientPhone
            )}`}
            className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white bg-[#091322] hover:bg-[#11223b] rounded shadow-md transition-colors"
          >
            {t('check_status_btn', 'Ariza holatini kuzatish')}
          </Link>
          <button
            onClick={() => {
              setConfirmedBooking(null);
              setStep(1);
              setFormData({
                serviceId: '',
                lawyerId: '',
                officeId: '',
                appointmentDate: '',
                startTime: '',
                clientName: '',
                clientPhone: '',
                clientEmail: '',
                clientReason: '',
              });
            }}
            className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
          >
            {t('book_another_btn', 'Yangi ariza qoldirish')}
          </button>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('nav_home', 'Bosh sahifaga qaytish')}
          </Link>
        </div>
      </div>
    );
  }

  if (loadingInitial) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
        <div className="w-8 h-8 border-2 border-[#c5a880] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        Ma’lumotlar yuklanmoqda...
      </div>
    );
  }

  const stepTitles = [
    t('step_1', 'Xizmat turi'),
    t('step_2', 'Advokat'),
    t('step_3', 'Ofis'),
    t('step_4', 'Sana & Vaqt'),
    t('step_5', 'Ma’lumotlar'),
    t('step_6', 'Sababi'),
    t('step_7', 'Tasdiqlash'),
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden max-w-4xl mx-auto">
      {/* Wizard Progress Stepper */}
      <div className="bg-[#091322] px-6 py-4 border-b border-[#1b2b46]">
        <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
          <span>{`Bosqich ${step} / 7: ${stepTitles[step - 1]}`}</span>
          <span className="font-serif text-[#c5a880] font-semibold tracking-wider">
            Advokat & Hamkorlar
          </span>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {[1, 2, 3, 4, 5, 6, 7].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                s === step
                  ? 'bg-[#c5a880]'
                  : s < step
                  ? 'bg-emerald-500'
                  : 'bg-slate-700'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="p-6 sm:p-10">
        {/* STEP 1: Select Service */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('select_service', 'Yuridik xizmat turini tanlang')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                Sizga kerak bo‘lgan huquqiy amaliyot sohasini belgilang.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((service) => {
                const isSelected = String(service.id) === String(formData.serviceId);
                return (
                  <div
                    key={service.id}
                    onClick={() => setFormData({ ...formData, serviceId: String(service.id) })}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#c5a880] bg-[#faf8f5] ring-2 ring-[#c5a880]/30 shadow-sm'
                        : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded ${isSelected ? 'bg-[#091322] text-[#c5a880]' : 'bg-slate-100 text-slate-600'}`}>
                        <Briefcase className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {localized(service, 'title')}
                        </h4>
                        <p className="text-slate-500 text-xs mt-1 line-clamp-2">
                          {localized(service, 'shortDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2: Select Lawyer */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('select_lawyer', 'Advokatni tanlang')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                Sizning ishingiz bo‘yicha konsultatsiya olib boruvchi mutaxassis.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {lawyers.map((lawyer) => {
                const isSelected = String(lawyer.id) === String(formData.lawyerId);
                return (
                  <div
                    key={lawyer.id}
                    onClick={() => setFormData({ ...formData, lawyerId: String(lawyer.id) })}
                    className={`p-4 rounded-lg border cursor-pointer transition-all flex items-center space-x-4 ${
                      isSelected
                        ? 'border-[#c5a880] bg-[#faf8f5] ring-2 ring-[#c5a880]/30 shadow-sm'
                        : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                    }`}
                  >
                    <img
                      src={lawyer.photoUrl}
                      alt={lawyer.fullName}
                      className="w-16 h-16 rounded-full object-cover border border-slate-300 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900 text-sm truncate">
                        {lawyer.fullName}
                      </h4>
                      <p className="text-xs text-[#b8935a] font-medium truncate">
                        {lawyer.position}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {lawyer.specialization}
                      </p>
                      <span className="inline-block text-[10px] text-slate-400 mt-1">
                        {lawyer.experienceYears} {t('lawyer_experience_suffix', 'yillik tajriba')}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: Select Office */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('select_office', 'Qabul ofisini tanlang')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                Uchrashuv o‘tkaziladigan qulay lokatsiyani tanlang.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {offices.map((office) => {
                const isSelected = String(office.id) === String(formData.officeId);
                return (
                  <div
                    key={office.id}
                    onClick={() => setFormData({ ...formData, officeId: String(office.id) })}
                    className={`p-5 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-[#c5a880] bg-[#faf8f5] ring-2 ring-[#c5a880]/30 shadow-sm'
                        : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded ${isSelected ? 'bg-[#091322] text-[#c5a880]' : 'bg-slate-100 text-slate-600'}`}>
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 text-base">
                          {office.name}
                        </h4>
                        <p className="text-slate-600 text-xs mt-1">
                          {office.addressUz || office.address}
                        </p>
                        <p className="text-slate-500 text-xs mt-2">
                          Telefon: {office.phone}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4: Date & Time Picker */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('select_date_time', 'Qulay sana va mavjud vaqt oralig‘ini belgilang')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                Advokatning ish jadvali asosida bo‘sh vaqtlar avtomatik ko‘rsatiladi.
              </p>
            </div>

            <div className="max-w-xs">
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Qabul sanasi
              </label>
              <input
                type="date"
                min={todayStr}
                value={formData.appointmentDate}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && val < todayStr) {
                    showToast('Bu sana o‘tib ketgan, iltimos boshqa sanani tanlang!', 'error');
                    return;
                  }
                  setFormData({ ...formData, appointmentDate: val, startTime: '' });
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-[#c5a880] focus:ring-1 focus:ring-[#c5a880]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">
                Mavjud vaqt oralig‘i
              </label>
              <TimeSlotPicker
                lawyerId={formData.lawyerId}
                officeId={formData.officeId}
                date={formData.appointmentDate}
                selectedTime={formData.startTime}
                onSelectTime={(time) => setFormData({ ...formData, startTime: time })}
              />
            </div>
          </div>
        )}

        {/* STEP 5: Client Information */}
        {step === 5 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('your_name', 'Mijoz ma’lumotlari')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                {currentLang === 'uz'
                  ? 'Uchrashuv tasdiqlanishi va bildirishnomalar yuborilishi uchun to‘g‘ri ma’lumotlarni kiriting.'
                  : currentLang === 'ru'
                  ? 'Введите корректные данные для подтверждения записи и отправки уведомлений.'
                  : 'Enter correct details for appointment confirmation and notifications.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  {t('client_name_label', 'To‘liq ism-familiyangiz')} *
                </label>
                <input
                  type="text"
                  placeholder="Masalan: Jamshid Aliyev"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:border-[#c5a880]"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  {t('phone_or_email_label', 'Telefon raqami')} *
                </label>
                <input
                  type="tel"
                  placeholder="+998 (97) 325-07-70"
                  value={formData.clientPhone || '+998 ()'}
                  onFocus={() => {
                    if (!formData.clientPhone) {
                      setFormData((p) => ({ ...p, clientPhone: '+998 ()' }));
                    }
                  }}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      clientPhone: formatUzPhone(e.target.value, prev.clientPhone),
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:outline-none focus:border-[#c5a880]"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Consultation Reason (Mandatory >= 10 chars) */}
        {step === 6 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                {t('consultation_reason', 'Murojaat sababi va mavzusi')}
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                {t('reason_hint', 'Kamida 10 ta belgi talab qilinadi. Bu advokatga oldindan tayyorgarlik ko‘rish imkonini beradi.')}
              </p>
            </div>

            <div>
              <textarea
                rows={5}
                value={formData.clientReason}
                onChange={(e) => setFormData({ ...formData, clientReason: e.target.value })}
                placeholder={t('reason_placeholder')}
                className="w-full px-4 py-3 border border-slate-300 rounded text-sm focus:outline-none focus:border-[#c5a880]"
                required
              />
              <div className="flex items-center justify-between text-xs mt-2">
                <span className={formData.clientReason.trim().length >= 10 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-medium'}>
                  Belgilar soni: {formData.clientReason.trim().length} / minimum 10
                </span>
                <span className="text-slate-400">
                  Advokat sir saqlash majburiyatiga ega (Konfidensial)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* STEP 7: Review & Confirm Summary */}
        {step === 7 && (
          <div className="space-y-6">
            <div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-slate-900">
                Arizani tekshirish va tasdiqlash
              </h3>
              <p className="text-slate-500 text-sm mt-1">
                Quyidagi ma’lumotlarni qayta tekshirib, arizani yuboring.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-xs uppercase text-slate-500 block">Xizmat turi</span>
                  <span className="font-semibold text-slate-900">{localized(selectedService, 'title')}</span>
                </div>
                <div>
                  <span className="text-xs uppercase text-slate-500 block">Advokat</span>
                  <span className="font-semibold text-slate-900">{selectedLawyer?.fullName}</span>
                </div>
                <div>
                  <span className="text-xs uppercase text-slate-500 block">Qabul ofisi</span>
                  <span className="font-semibold text-slate-900">{selectedOffice?.name}</span>
                </div>
                <div>
                  <span className="text-xs uppercase text-slate-500 block">Sana va vaqt</span>
                  <span className="font-semibold text-[#091322] font-mono">
                    {formData.appointmentDate} soat {formData.startTime}
                  </span>
                </div>
                <div>
                  <span className="text-xs uppercase text-slate-500 block">Mijoz</span>
                  <span className="font-medium text-slate-900">{formData.clientName} ({formData.clientPhone})</span>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200">
                <span className="text-xs uppercase text-slate-500 block mb-1">Murojaat sababi</span>
                <p className="text-slate-800 bg-white p-3 rounded border border-slate-200 italic text-xs leading-relaxed">
                  "{formData.clientReason}"
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Wizard Navigation Footer */}
        <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded text-slate-700 bg-white hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('btn_prev', 'Orqaga')}
            </button>
          ) : (
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded text-slate-700 bg-white hover:bg-slate-50 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('nav_home', 'Bosh sahifaga')}
            </Link>
          )}

          {step < 7 ? (
            <button
              type="button"
              disabled={!canProceed()}
              onClick={() => setStep(step + 1)}
              className={`inline-flex items-center px-6 py-2.5 text-sm font-semibold rounded transition-all ${
                canProceed()
                  ? 'bg-[#091322] text-[#c5a880] hover:bg-[#11223b] shadow-sm'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {t('btn_next', 'Davom etish')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={handleSubmit}
              className="inline-flex items-center px-8 py-3 text-sm font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-md transition-all active:scale-[0.98]"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#091322] border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('submitting', 'Yuborilmoqda...')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {t('btn_confirm_submit', 'Arizani rasmiylashtirish')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
